/**
 * Offers out right now, how that moved over four weeks, and where the case pipeline says it is
 * going (Theresa, Sep 29 2026).
 *
 * ⚠️ IT IS A TILE BECAUSE A BAR CANNOT SAY THIS. Offer sat in the stage chart as a 1% sliver, and
 * the one thing anyone wants from it — is that three going up or down — is invisible in a snapshot
 * by construction. A count, a line and a sentence answer it; a fourteenth bar never could.
 *
 * ⚠️ WHAT IT COUNTS IS NOT THE OFFER STAGE, and the footnote says so. "Reached the offer stage,
 * neither signed nor archived, within the last 90 days, on a role that is still open" is the only
 * rule a HISTORY can be built from — and the two narrowings take the figure from 20 to 2. What they
 * exclude is not lost: the whole backlog is reported in its own section at the bottom of the tab
 * (`OfferHygiene`), with a button that asks TA to tidy it.
 *
 * The sparkline is inline SVG — nothing in this repo ships a chart library, and this is four line
 * segments and five dots. Same call as `PlanWaterfall`.
 */
import { AlertCircle, ArrowDownRight, ArrowRight, ArrowUpRight, Minus } from 'lucide-react';
import type { OfferPulse } from '../../../types/openPositions';
import { offerTrend, type TrendDirection } from '../../../utils/offerPulse';
import { Card } from '../rolePrioritisation/prioritisationUi';

/*
 * ⚠️ NOT the traffic-light hues. More offers out is not "green" and fewer is not "red" — a quiet
 * offer week can be a well-run one, and the radar two sections up owns those three colours for
 * verdicts. The arrow carries the direction and the ink stays neutral, with Trusted Blue for the
 * figure itself.
 */
const TREND_ICON: Record<TrendDirection, typeof ArrowUpRight> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  steady: ArrowRight,
  unknown: Minus,
};

export function OfferTile({ pulse }: { pulse: OfferPulse | null | undefined }) {
  if (!pulse) {
    return (
      <Card className="flex h-full items-start gap-2 p-5 text-sm text-iron">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
        <span>
          The offer history could not be read. The stage charts beside it are unaffected — they come
          from a different query.
        </span>
      </Card>
    );
  }

  const trend = offerTrend(pulse);
  const Icon = TREND_ICON[trend.direction];

  return (
    <Card className="flex h-full flex-col p-5">
      <div className="text-xs font-medium text-iron">Offers out right now</div>
      <div className="mt-1 flex items-baseline gap-3">
        <span
          data-testid="offer-now"
          className="text-[44px] font-semibold leading-none tabular-nums text-trustedBlue"
        >
          {pulse.now}
        </span>
        <span className="text-xs leading-4 text-steel">
          awaiting an answer
          <br />
          on roles that are still open
        </span>
      </div>

      <Sparkline weeks={pulse.weeks} />

      <div className="mt-3 flex items-start gap-2 border-t border-solid border-pearl pt-3">
        <Icon
          className={`mt-0.5 h-4 w-4 shrink-0 ${
            trend.direction === 'unknown' ? 'text-steel' : 'text-asphalt'
          }`}
          aria-hidden
        />
        <p data-testid="offer-trend" className="text-[13px] leading-5 text-asphalt">
          <b className="font-semibold">
            {trend.direction === 'up'
              ? 'Expect more'
              : trend.direction === 'down'
                ? 'Expect fewer'
                : trend.direction === 'steady'
                  ? 'Expect about the same'
                  : 'No call either way'}
          </b>{' '}
          {trend.reason}
        </p>
      </div>

      {/*
        ⚠️ ONE LINE, and the task itself is at the bottom of the tab (Theresa, Sep 29 2026:
        "clean-up suggestions are great, but collapse them in a section at the bottom"). What stays
        here is only the part that explains THIS tile's number — a reader comparing it against
        Lever's own offer count needs to know a record is being held back and why.
      */}
      {pulse.hygiene.staleOnOpenRoles > 0 ? (
        <p data-testid="offer-stale" className="mt-2 text-[11px] leading-4 text-steel">
          Lever shows {pulse.now + pulse.hygiene.staleOnOpenRoles}: {pulse.hygiene.staleOnOpenRoles}{' '}
          more on an open role {pulse.hygiene.staleOnOpenRoles === 1 ? 'has' : 'have'} been sitting
          there over {pulse.windowDays} days. See the tidy-up at the bottom of this tab.
        </p>
      ) : null}
    </Card>
  );
}

/** Four weeks of offers out, oldest to newest. */
function Sparkline({ weeks }: { weeks: OfferPulse['weeks'] }) {
  if (weeks.length < 2) return null;

  const W = 260;
  const H = 52;
  const PAD = 6;
  /*
   * ⚠️ The floor of the scale is a MINIMUM of 1, not the series' own minimum. On counts this small
   * a range of 3→4 would otherwise fill the whole height and read as a doubling; anchoring the
   * bottom at zero keeps a one-offer move looking like a one-offer move.
   */
  const max = Math.max(...weeks.map((w) => w.offers), 1);
  const x = (i: number) => PAD + (i * (W - PAD * 2)) / (weeks.length - 1);
  const y = (v: number) => H - PAD - (v / max) * (H - PAD * 2);
  const points = weeks.map((w, i) => `${x(i)},${y(w.offers)}`).join(' ');

  const label = weeks.map((w) => w.offers).join(', ');

  return (
    <div className="mt-3">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-[52px] w-full"
        role="img"
        aria-label={`Offers out over the last ${weeks.length - 1} weeks, oldest first: ${label}`}
      >
        <polyline points={points} fill="none" stroke="#0B69D4" strokeWidth={2} strokeLinejoin="round" />
        {weeks.map((w, i) => (
          <circle
            key={w.weeksAgo}
            cx={x(i)}
            cy={y(w.offers)}
            r={i === weeks.length - 1 ? 4 : 2.5}
            fill={i === weeks.length - 1 ? '#0B69D4' : '#FFFFFF'}
            stroke="#0B69D4"
            strokeWidth={1.5}
          />
        ))}
      </svg>
      <div className="flex justify-between text-[11px] text-steel">
        <span>{weeks.length - 1} weeks ago</span>
        <span>now</span>
      </div>
    </div>
  );
}
